import { useCallback, useState } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { ICollection } from "../../../utils/interfaces/collection.interface";
import { handleBackendError } from "../../../utils/helpers";
import { useTranslation } from "react-i18next";
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface";
import { Lead } from "../../../models/lead";
import { IResource } from "../../../utils/interfaces/resource.interface";
import LeadRepository from "../../../repositories/lead-repository";
import { LeadCountDataList, LeadDataList } from "../list/interface";

export const useGetLead = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<LeadDataList[]>([]);
  const [totalData, setTotalData] = useState<number>(0);
  const [isOnFetchingData, setIsOnFetchingData] = useState<boolean>(false);

  const [countData, setCountData] = useState<LeadCountDataList[]>([]);
  const [isOnFetchingCountData, setIsOnFetchingCountData] =
    useState<boolean>(false);

  const [detailData, setDetailData] = useState<Lead | null>(null);
  const [isOnFetchingDetailData, setIsOnFetchingDetailData] =
    useState<boolean>(false);

  const loadData = useCallback(async (params: IPaginationParams) => {
    setIsOnFetchingData(true);

    await LeadRepository.all(params)
      .then((response: AxiosResponse<ICollection<LeadDataList[]>>) => {
        setData(response.data.data);
        setTotalData(response.data.meta.total);
      })
      .catch(() => {})
      .finally(() => {
        setIsOnFetchingData(false);
      });
  }, []);

  const loadCountData = useCallback(async () => {
    setIsOnFetchingCountData(true);

    await LeadRepository.allCount()
      .then((response: AxiosResponse<IResource<LeadCountDataList[]>>) => {
        setCountData(response.data.data);
      })
      .catch(() => {})
      .finally(() => {
        setIsOnFetchingCountData(false);
      });
  }, []);

  const loadDetailData = useCallback(
    async (id: string, params: any = null) => {
      setIsOnFetchingDetailData(true);

      await LeadRepository.show(id, params)
        .then((response: AxiosResponse<IResource<Lead>>) => {
          setDetailData(response.data.data);
        })
        .catch((e: AxiosError) => {
          handleBackendError(e, t("notification.error.default"));
        })
        .finally(() => {
          setIsOnFetchingDetailData(false);
        });
    },
    [t]
  );

  return {
    data,
    totalData,
    loadData,
    isOnFetchingData,
    loadDetailData,
    isOnFetchingDetailData,
    detailData,
    countData,
    isOnFetchingCountData,
    loadCountData,
  };
};
